// frontend/src/components/common/ConfirmDialog.jsx
import { Fragment, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  FormControlLabel, Radio, RadioGroup,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CONFIRM_VARIANTS } from './confirmVariants';
import AppDatePicker from './AppDatePicker';
import FieldLine from './FieldLine';
import { useHeldWhileClosed } from '../../hooks/useHeldWhileClosed';
import './ConfirmDialog.css';

/** Parse a message string into structured blocks.
 *  - Paragraphs are separated by `\n\n`
 *  - Lines starting with `• ` within a paragraph become bullet items
 */
const parseMessage = (message) => {
  if (!message) return [];
  return message.split('\n\n').map((block) => {
    const lines = block.split('\n');
    const bullets = lines.filter((l) => l.startsWith('• '));
    if (bullets.length > 0) {
      const plain = lines.filter((l) => !l.startsWith('• ')).join(' ').trim();
      return { type: 'bullets', intro: plain || null, items: bullets.map((b) => b.slice(2)) };
    }
    return { type: 'text', content: block };
  });
};

/** Both buttons always render. An omitted `confirmLabel` / `cancelLabel` falls
 *  back to the generic verb; pass a specific one wherever "Cancel" would read
 *  as an action on the object rather than as dismissal (see the purchase-order
 *  close / cancel gates in pages/PurchaseOrderDetail.jsx).
 *
 *  `input: { label, placeholder? }` adds one text field (a cancellation
 *  reason) and hands its trimmed value to `onConfirm`. No length cap here:
 *  the server is the one place that bounds it and refuses loudly
 *  (po_reason_too_long), which the caller surfaces like any other error.
 *
 *  `choices: [{ value, label }, …]` (two or more, string values — a radio
 *  hands its value back as a string) asks the question as a radio list, the
 *  first option picked, and hands the picked `value` to `onConfirm`.
 *
 *  `date: { label, initial, min }` asks for one day, starting at `initial`
 *  and refusing a day before `min` (all 'YYYY-MM-DD'), and hands the picked
 *  day to `onConfirm`; confirming waits while the field holds no acceptable
 *  day (empty, unfinished or before `min`).
 *
 *  A gate takes at most one of `input`, `choices` and `date`.
 */
const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  variant = 'warning',
  loading = false,
  input = null,
  choices = null,
  date = null,
  onConfirm,
  onCancel,
}) => {
  // Held through the fade-out: callers clear what they passed the moment the
  // popup closes. Flat primitives, as useHeldWhileClosed compares them.
  const live = {
    title, message, confirmLabel, cancelLabel, variant,
    hasInput: input != null, inputLabel: input?.label, inputPlaceholder: input?.placeholder,
    choices,
    hasDate: date != null, dateLabel: date?.label, dateInitial: date?.initial, dateMin: date?.min,
  };
  const shown = useHeldWhileClosed(open, live) ?? live;

  // An unrecognised variant raises rather than quietly rendering as `warning`:
  // it would otherwise show the wrong icon and the wrong tint for whatever the
  // caller meant. Same shape as themeFamily() in theme.js.
  if (!(shown.variant in CONFIRM_VARIANTS)) {
    throw new Error(`ConfirmDialog: unknown variant "${shown.variant}"`);
  }
  // The title sits in the top bar; the body beside the icon is the message.
  // Without one the popup shows a bare icon. Checked only while open —
  // useConfirmDialog holds no options before its first gate.
  if (open && !message) {
    throw new Error('ConfirmDialog: a message is required');
  }
  if (open && choices != null && (
    choices.length < 2 || choices.some((c) => typeof c.value !== 'string')
  )) {
    throw new Error('ConfirmDialog: choices take two or more string-valued options');
  }
  if (open && [input, choices, date].filter((asked) => asked != null).length > 1) {
    throw new Error('ConfirmDialog: a gate asks at most one of input, choices and date');
  }

  return (
    <Dialog
      open={open}
      className={`dialog-form dialog-form--confirm confirm-dialog confirm-dialog--${shown.variant}`}
      onClose={loading ? undefined : onCancel}
    >
      {/* Keyed on `open` so the text field starts empty on every gate — the
          dialog component itself stays mounted between gates. */}
      <ConfirmDialogBody
        key={String(open)}
        variant={shown.variant}
        title={shown.title}
        message={shown.message}
        confirmLabel={shown.confirmLabel}
        cancelLabel={shown.cancelLabel}
        // A fading popup has been answered: its buttons no longer act.
        loading={loading || !open}
        input={shown.hasInput ? { label: shown.inputLabel, placeholder: shown.inputPlaceholder } : null}
        choices={shown.choices}
        date={shown.hasDate ? { label: shown.dateLabel, initial: shown.dateInitial, min: shown.dateMin } : null}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </Dialog>
  );
};

function ConfirmDialogBody({
  variant, title, message, confirmLabel, cancelLabel, loading, input, choices, date, onConfirm, onCancel,
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [picked, setPicked] = useState(choices ? choices[0].value : null);
  const [day, setDay] = useState(date ? date.initial : '');
  const [dayRefused, setDayRefused] = useState(false);
  const blocks = parseMessage(message);

  return (
    <>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <div className="confirm-dialog-row">
          <div className={`confirm-dialog-icon confirm-dialog-icon--${variant}`}>
            {CONFIRM_VARIANTS[variant].icon}
          </div>
          <div className="confirm-dialog-text">
            {blocks.map((block, i) =>
              block.type === 'bullets' ? (
                <Fragment key={i}>
                  {block.intro && <p>{block.intro}</p>}
                  <ul>
                    {block.items.map((item, j) => <li key={j}>{item}</li>)}
                  </ul>
                </Fragment>
              ) : (
                <p key={i}>{block.content}</p>
              )
            )}
            {input && (
              <TextField
                label={input.label}
                placeholder={input.placeholder}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                size="small"
                fullWidth
                multiline
                minRows={2}
                disabled={loading}
              />
            )}
            {choices && (
              <RadioGroup
                value={picked}
                onChange={(e) => setPicked(e.target.value)}
              >
                {choices.map((choice) => (
                  <FormControlLabel
                    key={choice.value}
                    value={choice.value}
                    control={<Radio size="small" />}
                    label={choice.label}
                    disabled={loading}
                  />
                ))}
              </RadioGroup>
            )}
            {/* Read like the message's own "Label: value" lines, the field
                in the value's place. */}
            {date && (
              <FieldLine label={`${date.label}:`}>
                <AppDatePicker
                  value={day}
                  onChange={setDay}
                  onRefusedChange={setDayRefused}
                  minDate={date.min}
                  disabled={loading}
                />
              </FieldLine>
            )}
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={loading}
        >
          {cancelLabel ?? t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color={CONFIRM_VARIANTS[variant].color}
          onClick={() => onConfirm(input ? value.trim() : choices ? picked : date ? day : undefined)}
          disabled={loading || (date != null && (!day || dayRefused))}
        >
          {confirmLabel ?? t('common.confirm')}
        </Button>
      </DialogActions>
    </>
  );
}

export default ConfirmDialog;
